/**
 * Content file formats: the seed, level manifests and the content index.
 *
 * These are public contracts (CLAUDE.md rule 11): they change only additively
 * or behind a `formatVersion` bump. The World converts manifests into runtime
 * entities. This file only describes what's on disk.
 */

export const SEED_FORMAT_VERSION = 1;
/** 2: Mesh Renderer's one `mesh` field. 3: Canvas and Text for World UI. manifestUpgrades.ts reads older ones. */
export const MANIFEST_FORMAT_VERSION = 5;
export const INDEX_FORMAT_VERSION = 1;

// ─── Seed (content/content.seed.json) ──────────────────────────────────────

export interface ContentSeed {
  readonly formatVersion: number;
  readonly profile: SeedProfile;
  readonly roles: readonly SeedRole[];
  readonly projects: readonly SeedProject[];
  /** Help › About's credits and notices (additive to seed format 1). */
  readonly about?: SeedAbout;
  /** Schools, each its own level in the Education folder (additive to seed format 1). */
  readonly education?: readonly SeedSchool[];
}

export interface SeedAbout {
  readonly credits: readonly SeedCredit[];
  /** Paragraphs, shown as written (trademark and fan-content notices). */
  readonly notices: readonly string[];
}

/** A third-party asset's credit: what a Creative Commons license asks a user to give. */
export interface SeedCredit {
  /** What the asset is in this site, e.g. "Summoner's Rift map". */
  readonly subject: string;
  /** The work's own title, when it has one. */
  readonly title?: string;
  readonly author: string;
  /** Where it's from, e.g. "Sketchfab". */
  readonly source?: string;
  /** The work's page. */
  readonly url: string;
  /** The license's short name, e.g. "CC BY-NC-ND 4.0". */
  readonly license: string;
  readonly licenseUrl?: string;
}

export interface SeedProfile {
  readonly name: string;
  readonly headline: string;
  readonly location: string;
  readonly links: { readonly github?: string; readonly linkedin?: string };
  /** Site-absolute path; resolved against the Pages base at runtime (A-04). */
  readonly resume: string;
  readonly education: readonly { school: string; degree: string; dates: string }[];
  /** The owner's greeting in the tour offer, in their own voice (additive to seed format 1). */
  readonly welcome?: SeedWelcome;
}

export interface SeedWelcome {
  /** The offer's title, e.g. "Hi, I'm Kashif." */
  readonly title: string;
  /** Paragraphs, in order. */
  readonly body: readonly string[];
}

export interface SeedRole {
  readonly level: string;
  readonly company: string;
  readonly location: string;
  readonly summary?: string;
  readonly zones: readonly SeedZone[];
}

export interface SeedZone {
  readonly id: string;
  readonly title: string;
  readonly team?: string;
  readonly scope?: string;
  /** `YYYY-MM`. */
  readonly start: string;
  /** `YYYY-MM`, or null for the current role ("Present"). */
  readonly end: string | null;
  readonly highlights: readonly string[];
}

export interface SeedProject {
  readonly level: string;
  readonly name: string;
  readonly tagline: string;
  readonly summary: string;
  readonly highlights?: readonly string[];
  readonly links: Readonly<Record<string, string>>;
}

/** A school: its own level, with a zone per degree, in the Education folder. */
export interface SeedSchool {
  readonly level: string;
  /** The school's name, which each degree's panel shows. */
  readonly name: string;
  readonly tagline: string;
  readonly summary: string;
  /** In order: each gets a zone. */
  readonly degrees: readonly SeedDegree[];
}

/** One degree: the Degree component's fields (the school comes from the school's entry). */
export interface SeedDegree {
  readonly id: string;
  /** e.g. "B.S. Computer Science". */
  readonly degree: string;
  readonly concentration: string;
  /** As shown, e.g. "2014 - 2018". */
  readonly years: string;
  readonly highlights: readonly string[];
}

// ─── Level manifests (content/levels/<id>.level.json) ──────────────────────

export type LevelKind = "role" | "project" | "education";

export interface LevelManifest {
  readonly formatVersion: number;
  readonly id: string;
  readonly kind: LevelKind;
  /** Asset name as shown in the editor, e.g. `Riot_Games`. */
  readonly name: string;
  /** Display title, e.g. `Riot Games`. */
  readonly title: string;
  /** Overrides the tile subtitle derived from the seed (D-1). */
  readonly subtitle?: string;
  readonly thumbnail?: string;
  readonly entities: readonly ManifestEntity[];
}

export interface ManifestEntity {
  /** Stable, level-unique id. Deep links resolve through it (A-37). */
  readonly id: string;
  readonly name: string;
  /** Class label shown in the Inspector header, like an actor class (e.g. `RoleZone`). */
  readonly class?: string;
  /** Omitted means visible. */
  readonly visible?: boolean;
  /** Component type → props. Values may be seed refs before resolution. */
  readonly components?: Readonly<Record<string, unknown>>;
  /** Components turned off, their data kept (additive to format 1; omitted means none). */
  readonly disabled?: readonly string[];
  readonly children?: readonly ManifestEntity[];
}

// ─── Content index (generated; served as content/index.json) ───────────────

export interface ContentIndex {
  readonly formatVersion: number;
  /** Levels in Content Browser order: roles newest first, then projects. */
  readonly levels: readonly LevelIndexEntry[];
  readonly resume: { readonly path: string; readonly bytes: number } | null;
  /** Everything under `content/assets/`, by path, for asset pickers (built at build time). */
  readonly assets: readonly AssetIndexEntry[];
  /**
   * Rendered thumbnails of the engine's built-in assets (`gen:thumbs`), by
   * engine path, e.g. `engine/shapes/Box` → `thumbs/engine/shapes/Box.webp`.
   * Additive to index format 1.
   */
  readonly engineThumbnails?: Readonly<Record<string, string>>;
  /** Models that are skins of one character (`*.skins.json`), for the Skin picker. Additive to format 1. */
  readonly skinSets?: readonly SkinSet[];
}

/** The skin set file format (`*.skins.json`); a newer one is rejected, as manifests are. */
export const SKIN_SET_FORMAT_VERSION = 1;

/**
 * Models that are skins of one character. Each skin is a whole model with its
 * own animations; the set only names and groups them.
 */
export interface SkinSet {
  /** The set file's content path, e.g. `assets/models/league_ahri.skins.json`. */
  readonly path: string;
  /** The character, e.g. Ahri. */
  readonly name: string;
  /** In the set file's order: each model's content path and the skin's name. */
  readonly skins: readonly { readonly model: string; readonly name: string }[];
}

/** What an asset costs once loaded, read at build time (the Statistics panel's figures). */
/** A texture's size, and whether it stays compressed on the GPU (KTX2; additive to index format 1). */
export interface TextureSize {
  readonly width: number;
  readonly height: number;
  readonly compressed?: true;
}

export interface AssetStats {
  readonly triangles: number;
  readonly vertices: number;
  /** Vertex attributes and indices, as uploaded. */
  readonly geometryBytes: number;
  /** Embedded or standalone images, by size; their memory is estimated from these. */
  readonly textures: readonly TextureSize[];
  /**
   * A model's bounds in its own space, in meters, before skinning or
   * animation (additive to index format 1; absent for images).
   */
  readonly bounds?: {
    readonly min: readonly [number, number, number];
    readonly max: readonly [number, number, number];
  };
}

/** One asset file: what an `assetRef` prop can point at. */
export interface AssetIndexEntry {
  /** Content-relative path, e.g. `assets/models/league_ahri.glb`: the value an `assetRef` holds. */
  readonly path: string;
  /** The loader kind, matching `assetRef` props' `kind` (e.g. `gltf`). */
  readonly kind: string;
  readonly bytes: number;
  /** Animation clip names in file order (glTF only; otherwise empty). */
  readonly clips: readonly string[];
  /** Load cost, when the file could be read (additive to index format 1). */
  readonly stats?: AssetStats;
  /** A rendered preview on a transparent background (`gen:thumbs`), when one exists. */
  readonly thumbnail?: string;
}

/** One title held at a company, e.g. Staff Software Engineer, 2025 – now. */
export interface LevelPosition {
  readonly title: string;
  readonly years: string;
}

export interface LevelIndexEntry {
  readonly id: string;
  readonly kind: LevelKind;
  readonly name: string;
  readonly title: string;
  readonly subtitle: string;
  /** A role's titles with their years, newest first (derived from the seed); absent for projects. */
  readonly positions?: readonly LevelPosition[];
  /** Content-relative manifest path, e.g. `levels/riot-games.level.json`. */
  readonly path: string;
  /** Virtual asset path shown in the editor, e.g. `/Roles/Riot_Games.level` (A-27). */
  readonly assetPath: string;
  readonly bytes: number;
  readonly hash: string;
  readonly thumbnail?: string;
  /** Rendered thumbnails for each theme mode (`gen:thumbs`), when the manifest names none. */
  readonly thumbnails?: { readonly dark: string; readonly light: string };
}

/** The Content Browser folder a level kind lives in. */
/** The Content Browser's level folders, in order: what `/Content` lists before the assets. */
export const LEVEL_FOLDERS = ["Roles", "Projects", "Education"] as const;
export type LevelFolder = (typeof LEVEL_FOLDERS)[number];

const FOLDER_OF_KIND: Readonly<Record<LevelKind, LevelFolder>> = {
  role: "Roles",
  project: "Projects",
  education: "Education",
};

export function folderForKind(kind: LevelKind): LevelFolder {
  return FOLDER_OF_KIND[kind];
}
